import { useId } from 'react';
import { Label, RadioGroup, RadioGroupItem } from '@monksflow/monks-ui';

export interface BooleanFieldValue {
  value: boolean | null;
}

export interface BooleanFieldConfig {
  trueLabel?: string;
  falseLabel?: string;
}

interface BooleanFieldProps {
  value: BooleanFieldValue | null;
  config: BooleanFieldConfig;
  disabled?: boolean;
  onChange: (next: BooleanFieldValue) => void;
}

// RadioGroup's own value protocol (a string), not copy — never rendered as text.
// eslint-disable-next-line i18next/no-literal-string
const TRUE_RADIO_VALUE = 'true';
// eslint-disable-next-line i18next/no-literal-string
const FALSE_RADIO_VALUE = 'false';

/**
 * Pure, controlled. Always two radios — never a toggle/switch — per the prototype and the
 * spec's boolean scenario. `null` renders with neither radio pre-selected ("unanswered" is a
 * real, distinct state, not defaulted to false).
 */
export function BooleanField({ value, config, disabled = false, onChange }: BooleanFieldProps) {
  const trueId = useId();
  const falseId = useId();
  const current = value?.value ?? null;
  // Seed defaults, not copy — same reasoning as `DEFAULT_SECTION_CONFIG.boolean` in
  // `entities/brief/lib/sectionTypes.ts`: pinned byte-for-byte to the backend's English
  // default; the author can rewrite both labels per section when authoring the template.
  // eslint-disable-next-line i18next/no-literal-string
  const trueLabel = config.trueLabel ?? 'Yes';
  // eslint-disable-next-line i18next/no-literal-string
  const falseLabel = config.falseLabel ?? 'No';
  const radioValue = current === null ? undefined : current ? TRUE_RADIO_VALUE : FALSE_RADIO_VALUE;

  return (
    <div className="flex flex-col gap-2">
      <RadioGroup
        value={radioValue}
        disabled={disabled}
        onValueChange={(next: string) => onChange({ value: next === TRUE_RADIO_VALUE })}
        className="flex items-center gap-4"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value={TRUE_RADIO_VALUE} id={trueId} disabled={disabled} />
          <Label htmlFor={trueId}>{trueLabel}</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value={FALSE_RADIO_VALUE} id={falseId} disabled={disabled} />
          <Label htmlFor={falseId}>{falseLabel}</Label>
        </div>
      </RadioGroup>
    </div>
  );
}
